import { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Users2, ClipboardList, Megaphone, MessageSquare, UserCircle2, ChevronRight, ArrowLeft } from 'lucide-react';
import { P2PProvider, useP2P } from '../../context/P2PContext';
import Layout from '../../components/Layout/Layout';
import '../../styles/p2p.css';

import Market from './Market';
import OrderPlacement from './OrderPlacement';
import ActiveOrder from './ActiveOrder';
import OrderComplete from './OrderComplete';
import Dispute from './Dispute';
import OrderHistory from './OrderHistory';
import MyAds from './MyAds';
import PostAdStep1 from './PostAd/Step1';
import PostAdStep2 from './PostAd/Step2';
import PostAdStep3 from './PostAd/Step3';
import PostAdPreview from './PostAd/Preview';
import ChatList from './ChatList';
import P2PProfile from './Profile';
import PaymentMethods from './PaymentMethods';
import AddPaymentMethod from './AddPaymentMethod';
import SelectPaymentMethod from './SelectPaymentMethod';
import OrderDetail from './OrderDetail';
import DisclaimerModal from './DisclaimerModal';

const navItems = [
  { id: 'market',  label: 'P2P Market',  icon: Users2,        path: '/p2p' },
  { id: 'orders',  label: 'My Orders',   icon: ClipboardList, path: '/p2p/orders' },
  { id: 'ads',     label: 'My Ads',      icon: Megaphone,     path: '/p2p/ads' },
  { id: 'chat',    label: 'Chat',        icon: MessageSquare, path: '/p2p/chat' },
  { id: 'profile', label: 'Profile',     icon: UserCircle2,   path: '/p2p/profile' },
];

function useP2PActive() {
  const location = useLocation();
  return (path: string) => {
    if (path === '/p2p') return location.pathname === '/p2p';
    return location.pathname.startsWith(path);
  };
}

function P2PBottomNav() {
  const navigate = useNavigate();
  const isActive = useP2PActive();
  const location = useLocation();
  const { activeOrders, unreadCounts } = useP2P();
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);
  const onOrderScreen = /^\/p2p\/order\/[^/]+(\/.*)?$/.test(location.pathname);

  return (
    <>
      {activeOrders.length > 0 && !onOrderScreen && (
        <div className="p2p-active-order-bar" onClick={() => navigate(`/p2p/order/${activeOrders[0]._id}`)}>
          <span className="p2p-active-order-bar-text">⏱ Active order in progress</span>
          <span style={{ fontSize: 12, color: '#000' }}>View →</span>
        </div>
      )}
      <nav className="p2p-bottom-nav">
        {navItems.map(item => (
          <div key={item.id} className={`p2p-nav-item ${isActive(item.path) ? 'active' : ''}`} onClick={() => navigate(item.path)}>
            <div style={{ position: 'relative' }}>
              <item.icon size={22} />
              {item.id === 'chat' && totalUnread > 0 && (
                <span style={{ position: 'absolute', top: -6, right: -8, background: '#F6465D', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 9999, padding: '1px 5px', minWidth: 14, textAlign: 'center' }}>
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </div>
            <span>{item.label}</span>
          </div>
        ))}
      </nav>
    </>
  );
}

function P2PSidebar() {
  const navigate = useNavigate();
  const isActive = useP2PActive();
  const location = useLocation();
  const { activeOrders, unreadCounts } = useP2P();
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);
  const onOrderScreen = /^\/p2p\/order\/[^/]+(\/.*)?$/.test(location.pathname);

  return (
    <aside className="p2p-sidebar">
      <div className="p2p-sidebar-title">P2P Trading</div>
      <nav className="p2p-sidebar-nav">
        {navItems.map(item => (
          <div
            key={item.id}
            className={`p2p-sidebar-item ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            <item.icon size={17} />
            <span>{item.label}</span>
            {item.id === 'chat' && totalUnread > 0 && (
              <span className="p2p-sidebar-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
            )}
            <ChevronRight size={14} className="p2p-sidebar-chevron" />
          </div>
        ))}
      </nav>
      {activeOrders.length > 0 && !onOrderScreen && (
        <div className="p2p-sidebar-active-order" onClick={() => navigate(`/p2p/order/${activeOrders[0]._id}`)}>
          <span>⏱ Active order</span>
          <span>View →</span>
        </div>
      )}
    </aside>
  );
}

function DisclaimerGate({ children }: { children: React.ReactNode }) {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    try {
      const info = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (!info.p2pDisclaimerAccepted) setShowDisclaimer(true);
    } catch { /* ignore */ }
  }, []);

  const handleAccept = () => {
    try {
      const raw = localStorage.getItem('userInfo') || '{}';
      const info = JSON.parse(raw);
      info.p2pDisclaimerAccepted = true;
      localStorage.setItem('userInfo', JSON.stringify(info));
    } catch { /* ignore */ }
    setShowDisclaimer(false);
  };

  if (showDisclaimer) return <DisclaimerModal onAccept={handleAccept} onCancel={() => window.history.back()} />;
  return <>{children}</>;
}

const P2PRoutes = () => (
  <Routes>
    <Route path="/"                    element={<Market />} />
    <Route path="/buy/:adId"           element={<OrderPlacement />} />
    <Route path="/order/:id"           element={<ActiveOrder />} />
    <Route path="/order/:id/complete"  element={<OrderComplete />} />
    <Route path="/order/:id/dispute"   element={<Dispute />} />
    <Route path="/orders"              element={<OrderHistory />} />
    <Route path="/ads"                 element={<MyAds />} />
    <Route path="/post-ad/1"           element={<PostAdStep1 />} />
    <Route path="/post-ad/2"           element={<PostAdStep2 />} />
    <Route path="/post-ad/3"           element={<PostAdStep3 />} />
    <Route path="/post-ad/preview"     element={<PostAdPreview />} />
    <Route path="/chat"                element={<ChatList />} />
    <Route path="/profile"             element={<P2PProfile />} />
    <Route path="/profile/:userId"     element={<P2PProfile />} />
    <Route path="/payment-methods"     element={<PaymentMethods />} />
    <Route path="/payment-methods/add" element={<AddPaymentMethod />} />
    <Route path="/select-payment"      element={<SelectPaymentMethod />} />
    <Route path="/order/:id/detail"    element={<OrderDetail />} />
  </Routes>
);

function P2PShellInner() {
  return (
    <>
      {/* ── Desktop ── */}
      <div className="p2p-desktop-wrapper">
        <Layout activePage="p2p" hideMobileNav={true}>
          <div className="p2p-desktop-layout">
            <P2PSidebar />
            <main className="p2p-desktop-main">
              <DisclaimerGate><P2PRoutes /></DisclaimerGate>
            </main>
          </div>
        </Layout>
      </div>

      {/* ── Mobile ── */}
      <div className="p2p-mobile-wrapper">
        <div className="p2p-shell">
          <DisclaimerGate>
            <P2PRoutes />
            <P2PBottomNav />
          </DisclaimerGate>
        </div>
      </div>
    </>
  );
}

export default function P2PShell() {
  return (
    <P2PProvider>
      <P2PShellInner />
    </P2PProvider>
  );
}
